import { type ApiSchemas, call } from '@rental/contracts/client'
import {
  type InspectionStage,
  inspectionAction,
  MAX_DAMAGES_PER_INSPECTION,
  MAX_PHOTOS_PER_INSPECTION,
  type VehicleZone,
} from '@rental/domain'
import { format, formatDateTime, formatEuro, plural } from '@rental/i18n'
import { Button, cn, Field, FilePicker } from '@rental/ui'
import { useQuery } from '@tanstack/react-query'
import { Camera, Plus, RotateCcw, Trash2 } from 'lucide-react'
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useAccess } from '../access-context'
import { AUTH_MODE, api, load } from '../api'
import type { CanDo } from '../booking-can'
import {
  CaptureChip,
  canDiscard,
  canRetry,
  LocalImage,
  useDiscard,
  useWhy,
} from '../capture/CaptureUi'
import { useCapture } from '../capture/capture-context'
import { useInspectionItems } from '../capture/hooks'
import type { CaptureItem, DamageItem, InspectionKey } from '../capture/item'
import { StatusChip } from '../components/StatusChip'
import { Loading, ProblemNotice, StaffInput } from '../components/ui'
import { erasedWhen } from '../customer/privacy'
import { filePath } from '../files'
import { ImageViewer, PhotoThumb } from '../files-ui'
import { useLocale } from '../i18n'
import { photoToJpeg } from '../photos/jpeg'
import { problemOf } from '../problems'
import { reportError } from '../telemetry'

type Inspection = ApiSchemas['StaffInspection']
type Photo = ApiSchemas['StaffInspectionPhoto']
type Damage = ApiSchemas['StaffDamage']
type ErasedPhoto = ApiSchemas['StaffErasedPhoto']
type Messages = ReturnType<typeof useLocale>['messages']

export interface MarkedDamage {
  id: string
  zone: VehicleZone
  description: string
}

/** Each zone's place on the diagram: the vehicle seen from above, its front at the top. */
const PLACE: Readonly<Record<VehicleZone, string>> = {
  front: 'col-start-2 row-start-1',
  left: 'col-start-1 row-start-2',
  roof: 'col-start-2 row-start-2',
  seat: 'col-start-2 row-start-2',
  right: 'col-start-3 row-start-2',
  rear: 'col-start-2 row-start-3',
  interior: 'col-span-3 row-start-4',
}

const stored = (photos: readonly Photo[]) => photos.filter((photo) => photo.status === 'stored')

/** S8: how many photos were erased and when, in words; null when none were. */
function useErasedWords() {
  const { locale, messages: m } = useLocale()
  return (photos: readonly ErasedPhoto[]): string | null =>
    photos.length === 0
      ? null
      : plural(locale, m.inspection.erasedPhotos, photos.length, {
          when: erasedWhen(
            photos.map((photo) => photo.erasedAt),
            locale,
            m,
          ),
        })
}

/** Where a photo or a damage goes: this inspection, its zone, a damage it shows. */
interface Target {
  key: InspectionKey
  plate: string
  zone: VehicleZone
  damageId?: string
  damageItemId?: string
}

/** The iPad is full: the queue cannot keep another photo (S4). */
function isFull(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'QuotaExceededError'
}

/**
 * OPS-1, OPS-2 and J4: the damage diagram. At the hand-over the counter
 * photographs the vehicle zone by zone and marks the damage already there;
 * at the return the hand-over's photos sit beside the new ones, and a damage
 * marked now can be charged. Read-only once the vehicle is back, or for a
 * role that cannot hand over. A group of controls, never a form: it sits
 * inside the hand-over's and the return's forms.
 *
 * S4 (OPS-5): what is captured is kept on the iPad first and sent from
 * there, signal or not (capture/). Until the server lists it, it shows here
 * from the iPad, with where it stands.
 */
export function InspectionPanel({
  code,
  vehicleId,
  readOnly = false,
  can,
  onCharge,
  charged,
  chargesFull = false,
}: {
  code: string
  /**
   * S9: the booking's own answer. Photos and damages are the hand-over's at
   * its stage and the return's at its own: a trip opens only its leg's.
   */
  can: CanDo
  /** At a hand-over, the vehicle being chosen; otherwise the booking's own. */
  vehicleId?: string
  readOnly?: boolean
  /** At the return: puts a damage marked now into the charges being typed. */
  onCharge?: (damage: MarkedDamage) => void
  /** The damages already in those charges. */
  charged?: ReadonlySet<string>
  /** The charges are at their limit: nothing more can join them. */
  chargesFull?: boolean
}) {
  const { locale, messages: m } = useLocale()
  const { reconcile, onSent } = useCapture()
  const [zone, setZone] = useState<VehicleZone | null>(null)
  const query = useQuery({
    queryKey: ['inspection', code, vehicleId ?? null],
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/bookings/{code}/inspection', {
            params: { path: { code }, query: vehicleId ? { vehicleId } : {} },
          }),
        ),
      ),
  })
  const inspection = query.data
  const stage =
    inspection?.open && !readOnly && can(inspectionAction(inspection.open)) ? inspection.open : null
  const shownStage = inspection?.open ?? null
  const key = useMemo<InspectionKey | null>(
    () =>
      inspection && shownStage
        ? { bookingCode: code, stage: shownStage, vehicleId: inspection.vehicle.id }
        : null,
    [inspection, shownStage, code],
  )
  const kept = useInspectionItems(key)
  // What the server lists has arrived: the iPad's copy can go.
  const listed = useMemo(() => {
    if (!inspection) return null
    return {
      photos: new Set(inspection.photos.map((photo) => photo.id)),
      damages: new Set(inspection.damages.map((damage) => damage.id)),
    }
  }, [inspection])
  useEffect(() => {
    if (listed) reconcile(listed)
  }, [listed, reconcile])
  // A photo may name its damage by the iPad's item while the server lists it already.
  const damageOfItem = useMemo(
    () =>
      new Map(
        kept.flatMap((item) =>
          item.kind === 'damage' && item.serverId ? [[item.id, item.serverId] as const] : [],
        ),
      ),
    [kept],
  )
  // A damage marked here keeps its place (and its camera button) once the server lists it:
  // swapping the entry would drop a photo being taken at that moment.
  const damageKeys = useRef(new Map<string, string>())
  for (const item of kept) {
    if (item.kind === 'damage' && item.serverId && !damageKeys.current.has(item.serverId)) {
      damageKeys.current.set(item.serverId, item.id)
    }
  }
  const local = useMemo(
    () =>
      kept.filter(
        (item) =>
          !(
            item.serverId &&
            listed &&
            (item.kind === 'photo' ? listed.photos : listed.damages).has(item.serverId)
          ),
      ),
    [kept, listed],
  )

  // J4: at the return, a damage marked here joins the charges once the server knows it.
  const chargeOnSend = useRef(new Set<string>())
  const charge = useRef(onCharge)
  charge.current = onCharge
  useEffect(
    () =>
      onSent((item) => {
        if (item.kind !== 'damage' || !item.serverId || !chargeOnSend.current.has(item.id)) return
        chargeOnSend.current.delete(item.id)
        charge.current?.({ id: item.serverId, zone: item.zone, description: item.description })
      }),
    [onSent],
  )

  const problem = problemOf(query.error)
  if (!inspection) return problem ? <ProblemNotice problem={problem} /> : <Loading />
  const selected = zone && inspection.zones.includes(zone) ? zone : (inspection.zones[0] ?? null)
  const zoneName = (name: VehicleZone) => m.inspection.zones[name]
  const photosIn = (name: VehicleZone) =>
    stored(inspection.photos).filter((photo) => photo.zone === name).length +
    local.filter((item) => item.kind === 'photo' && item.zone === name).length
  const damagesIn = (name: VehicleZone) =>
    inspection.damages.filter((damage) => damage.zone === name).length +
    local.filter((item) => item.kind === 'damage' && item.zone === name).length
  // The server's limits, counted with what waits on the iPad: past them it would refuse.
  const photosFull =
    inspection.photos.filter((photo) => photo.stage === shownStage).length +
      local.filter((item) => item.kind === 'photo').length >=
    MAX_PHOTOS_PER_INSPECTION
  const damagesFull =
    inspection.damages.filter(
      (damage) => damage.foundAt === shownStage && damage.foundCode === code,
    ).length +
      local.filter((item) => item.kind === 'damage').length >=
    MAX_DAMAGES_PER_INSPECTION

  return (
    <div className="flex flex-col gap-6">
      <p className="text-text-muted">
        {stage === 'checkout'
          ? m.inspection.introCheckout
          : stage === 'return'
            ? m.inspection.introReturn
            : m.inspection.introClosed}
      </p>
      {/* S8: the zones count the photos kept; this says that others were, and went. */}
      {inspection.erasedPhotos.length > 0 ? (
        <p className="text-text-muted">
          {plural(locale, m.inspection.erasedSummary, inspection.erasedPhotos.length, {
            when: erasedWhen(
              inspection.erasedPhotos.map((photo) => photo.erasedAt),
              locale,
              m,
            ),
          })}
        </p>
      ) : null}
      <fieldset className="flex min-w-0 flex-col gap-3">
        <legend className="mb-3 font-bold">
          {format(m.inspection.diagram, { plate: inspection.vehicle.plate })}
        </legend>
        <div className="grid max-w-md grid-cols-3 gap-2">
          {inspection.zones.map((name) => {
            const photos = photosIn(name)
            const damages = damagesIn(name)
            return (
              <button
                key={name}
                type="button"
                aria-pressed={name === selected}
                aria-label={format(m.inspection.zoneLabel, {
                  zone: zoneName(name),
                  photos: plural(locale, m.inspection.photoCount, photos),
                  damages: plural(locale, m.inspection.damageCount, damages),
                })}
                onClick={() => setZone(name)}
                className={cn(
                  'flex min-h-touch flex-col items-center justify-center gap-1 rounded-sm border-2 p-2 text-center',
                  name === selected
                    ? 'border-brand bg-surface font-bold text-brand'
                    : 'border-text-muted bg-bg',
                  PLACE[name],
                )}
              >
                <span>{zoneName(name)}</span>
                <span className="flex flex-wrap justify-center gap-1 text-xs">
                  <span className="text-text-muted">
                    {plural(locale, m.inspection.photoCount, photos)}
                  </span>
                  {damages > 0 ? (
                    <StatusChip tone="warning">
                      {plural(locale, m.inspection.damageCount, damages)}
                    </StatusChip>
                  ) : null}
                </span>
              </button>
            )
          })}
        </div>
      </fieldset>

      {selected ? (
        <ZoneDetail
          // One zone's fields, sends and messages are its own: another zone starts afresh.
          key={`${inspection.vehicle.id}:${selected}`}
          code={code}
          inspection={inspection}
          zone={selected}
          stage={stage}
          local={local.filter((item) => item.zone === selected)}
          damageOfItem={damageOfItem}
          damageKeys={damageKeys.current}
          photosFull={photosFull}
          damagesFull={damagesFull}
          onQueued={
            stage === 'return' && onCharge
              ? (damage) => chargeOnSend.current.add(damage.id)
              : undefined
          }
          onCharge={onCharge}
          charged={charged}
          chargesFull={chargesFull}
        />
      ) : null}
    </div>
  )
}

function ZoneDetail({
  code,
  inspection,
  zone,
  stage,
  local,
  damageOfItem,
  damageKeys,
  photosFull,
  damagesFull,
  onQueued,
  onCharge,
  charged,
  chargesFull,
}: {
  code: string
  inspection: Inspection
  zone: VehicleZone
  stage: InspectionStage | null
  /** This zone's items kept on the iPad that the server does not list yet. */
  local: readonly CaptureItem[]
  /** A damage's server id by its item on this iPad, once sent. */
  damageOfItem: ReadonlyMap<string, string>
  /** The item a damage on the server was marked as here: its entry's key, kept. */
  damageKeys: ReadonlyMap<string, string>
  photosFull: boolean
  damagesFull: boolean
  onQueued?: ((damage: DamageItem) => void) | undefined
  onCharge?: ((damage: MarkedDamage) => void) | undefined
  charged?: ReadonlySet<string> | undefined
  chargesFull: boolean
}) {
  const { messages: m } = useLocale()
  const erasedWords = useErasedWords()
  const name = m.inspection.zones[zone]
  const inZone = stored(inspection.photos).filter((photo) => photo.zone === zone)
  // Before the vehicle leaves there is nothing to compare with: one column.
  const columns: InspectionStage[] =
    inspection.open === 'checkout' ? ['checkout'] : ['checkout', 'return']
  const damages = inspection.damages.filter((damage) => damage.zone === zone)
  const stageName = (stage: InspectionStage) =>
    stage === 'checkout' ? m.inspection.atCheckout : m.inspection.atReturn
  const target = (extra: Partial<Target> = {}): Target => ({
    key: { bookingCode: code, stage: stage ?? 'checkout', vehicleId: inspection.vehicle.id },
    plate: inspection.vehicle.plate,
    zone,
    ...extra,
  })
  const zonePhotos = local.filter(
    (item) => item.kind === 'photo' && !item.damageId && !item.damageItemId,
  )
  const photosOf = (damageId: string | null, itemId: string | null) =>
    local.filter((item) => {
      if (item.kind !== 'photo') return false
      const shows =
        item.damageId ?? (item.damageItemId ? damageOfItem.get(item.damageItemId) : undefined)
      return (
        (damageId !== null && shows === damageId) ||
        (itemId !== null && item.damageItemId === itemId)
      )
    })
  const localDamages = local.filter((item): item is DamageItem => item.kind === 'damage')

  return (
    <section className="flex flex-col gap-6 border-t border-rule pt-4">
      <h3 className="text-lg">{name}</h3>
      <div className={cn('grid gap-4', columns.length > 1 ? 'sm:grid-cols-2' : '')}>
        {columns.map((column) => {
          const photos = inZone.filter((photo) => photo.stage === column)
          const kept = column === inspection.open ? zonePhotos : []
          // S8: photos erased since: "none" would be untrue, there were some.
          const erased = erasedWords(
            inspection.erasedPhotos.filter(
              (photo) => photo.zone === zone && photo.stage === column,
            ),
          )
          const thumbs = photos.length > 0 || kept.length === 0
          const erasedInBox = thumbs && photos.length === 0 && erased !== null
          return (
            <div key={column} className="flex flex-col gap-2 rounded-sm bg-surface-well p-4">
              <h4 className="text-lg font-bold text-brand-strong">{stageName(column)}</h4>
              {thumbs ? (
                <Thumbs
                  wide={columns.length === 1}
                  photos={photos}
                  alt={(_, index) =>
                    format(m.inspection.photoAlt, {
                      zone: name,
                      stage: stageName(column).toLowerCase(),
                      number: index + 1,
                    })
                  }
                  empty={erasedInBox ? erased : m.inspection.noPhotos}
                />
              ) : null}
              {erased !== null && !erasedInBox ? (
                <p className="text-sm text-text-muted">{erased}</p>
              ) : null}
              <LocalPhotos
                wide={columns.length === 1}
                items={kept}
                alt={(index) =>
                  format(m.inspection.photoAlt, {
                    zone: name,
                    stage: stageName(column).toLowerCase(),
                    number: photos.length + index + 1,
                  })
                }
              />
            </div>
          )
        })}
      </div>
      {stage ? (
        <PhotoField
          label={format(m.inspection.addPhoto, { zone: name })}
          target={target()}
          full={photosFull}
        />
      ) : null}

      <div className="flex flex-col gap-3">
        <h4 className="text-lg font-bold text-brand-strong">{m.inspection.damagesTitle}</h4>
        {damages.length === 0 && localDamages.length === 0 ? (
          <p className="text-text-muted">{m.inspection.noDamages}</p>
        ) : null}
        <ul className="flex flex-col gap-4">
          {[
            ...damages.map((damage) => ({
              key: damageKeys.get(damage.id) ?? damage.id,
              server: damage,
              item: null,
            })),
            ...localDamages.map((item) => ({ key: item.id, server: null, item })),
          ].map((entry) => {
            const serverId = entry.server?.id ?? entry.item?.serverId ?? null
            const description = entry.server?.description ?? entry.item?.description ?? ''
            return (
              <DamageEntry
                key={entry.key}
                server={entry.server}
                item={entry.item}
                erased={
                  entry.server
                    ? inspection.erasedPhotos.filter((photo) => photo.damageId === entry.server?.id)
                    : []
                }
                zoneName={name}
                photos={photosOf(serverId, entry.item?.id ?? null)}
                photoField={
                  stage ? (
                    <PhotoField
                      label={format(m.inspection.damagePhoto, { description })}
                      target={target(
                        serverId
                          ? { damageId: serverId }
                          : { damageItemId: entry.item?.id ?? entry.key },
                      )}
                      full={photosFull}
                    />
                  ) : null
                }
                onCharge={onCharge}
                charged={charged}
                chargesFull={chargesFull}
              />
            )
          })}
        </ul>
        {stage ? <MarkDamage target={target()} full={damagesFull} onQueued={onQueued} /> : null}
      </div>
    </section>
  )
}

/**
 * One damage on the zone: as the server lists it, or (S4) as marked on this
 * iPad and not listed yet, with where it stands and what its person can do.
 * One component for both, every part in a fixed place: the entry, and the
 * camera button in it, live on when the server starts listing the damage.
 */
function DamageEntry({
  server,
  item,
  erased,
  zoneName,
  photos,
  photoField,
  onCharge,
  charged,
  chargesFull,
}: {
  server: Damage | null
  item: DamageItem | null
  /** S8: this booking's photos of it erased since. */
  erased: readonly ErasedPhoto[]
  zoneName: string
  photos: readonly CaptureItem[]
  photoField: ReactNode
  onCharge?: ((damage: MarkedDamage) => void) | undefined
  charged?: ReadonlySet<string> | undefined
  chargesFull: boolean
}) {
  const { locale, messages: m } = useLocale()
  const whyOf = useWhy()
  const erasedWords = useErasedWords()
  const { access } = useAccess()
  const { retry } = useCapture()
  const { ask, dialog } = useDiscard()
  const who = access.state === 'unlocked' ? access.session.user.id : null
  const descriptionId = useId()
  const description = server?.description ?? item?.description ?? ''
  const pending = server ? null : item
  const why = pending ? whyOf(pending) : null
  const alt = () => format(m.inspection.damagePhotoAlt, { zone: zoneName, description })
  const erasedLine = erasedWords(erased)
  return (
    <li className="flex flex-col gap-3 rounded-sm bg-surface-well p-4">
      <p className="flex flex-wrap items-center gap-2">
        <span id={descriptionId} className="font-medium">
          {description}
        </span>
        {server ? (
          <StatusChip tone={server.newOnThisRental ? 'danger' : 'warning'}>
            {server.newOnThisRental ? m.inspection.newHere : m.inspection.existing}
          </StatusChip>
        ) : pending ? (
          <CaptureChip item={pending} />
        ) : null}
        {server && onCharge && server.chargedCents === null && charged?.has(server.id) ? (
          <StatusChip tone="info">{m.inspection.inCharges}</StatusChip>
        ) : null}
        {server ? (
          <span className="text-sm text-text-muted">
            {format(m.inspection.foundOn, { date: formatDateTime(locale, server.foundOn) })}
          </span>
        ) : null}
        {server && server.chargedCents !== null ? (
          <span className="text-sm">
            {format(m.inspection.charged, { amount: formatEuro(server.chargedCents, locale) })}
          </span>
        ) : null}
      </p>
      {why ? <p className="text-sm">{why}</p> : null}
      {server && (stored(server.photos).length > 0 || photos.length === 0) ? (
        <Thumbs
          wide
          photos={stored(server.photos)}
          alt={alt}
          empty={erasedLine ?? m.inspection.noPhotos}
        />
      ) : null}
      {erasedLine !== null && server && stored(server.photos).length > 0 ? (
        <p className="text-sm text-text-muted">{erasedLine}</p>
      ) : null}
      <LocalPhotos wide items={photos} alt={alt} />
      {photoField || onCharge || pending ? (
        <div className="flex flex-wrap items-start gap-3">
          {photoField}
          {server ? (
            <ChargeAction
              damage={server}
              m={m}
              onCharge={onCharge}
              charged={charged}
              full={chargesFull}
            />
          ) : null}
          {/* Several damages can each have both: the description says which one. */}
          {pending && canRetry(pending) && pending.capturedBy.userId === who ? (
            <Button
              variant="outline"
              className="rounded-sm"
              aria-describedby={descriptionId}
              onClick={() => void retry(pending.id)}
            >
              <RotateCcw aria-hidden="true" className="size-4" />
              {m.capture.retry}
            </Button>
          ) : null}
          {pending && canDiscard(pending, who) ? (
            <Button
              variant="outline"
              className="rounded-sm"
              aria-describedby={descriptionId}
              onClick={() => ask([pending.id])}
            >
              <Trash2 aria-hidden="true" className="size-4" />
              {m.capture.discard}
            </Button>
          ) : null}
        </div>
      ) : null}
      {dialog}
    </li>
  )
}

/** S4: photos kept on this iPad, each with where it stands. */
function LocalPhotos({
  items,
  alt,
  wide = false,
}: {
  items: readonly CaptureItem[]
  alt: (index: number) => string
  wide?: boolean
}) {
  const { messages: m } = useLocale()
  const why = useWhy()
  const { access } = useAccess()
  const { retry } = useCapture()
  const { ask, dialog } = useDiscard()
  const photoId = useId()
  const who = access.state === 'unlocked' ? access.session.user.id : null
  if (items.length === 0) return null
  return (
    <>
      <ul className={cn('grid gap-2', wide ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2')}>
        {items.map((item, index) => {
          const reason = why(item)
          // The photo's own words name its buttons: the image is not drawn until it is read.
          const named = `${photoId}-${index}`
          return (
            <li key={item.id} className="flex flex-col items-start gap-1">
              <LocalImage item={item} alt={alt(index)} />
              <span id={named} hidden>
                {alt(index)}
              </span>
              <CaptureChip item={item} />
              {reason ? <p className="text-sm">{reason}</p> : null}
              {canRetry(item) && item.capturedBy.userId === who ? (
                <Button
                  variant="outline"
                  className="rounded-sm"
                  aria-describedby={named}
                  onClick={() => void retry(item.id)}
                >
                  <RotateCcw aria-hidden="true" className="size-4" />
                  {m.capture.retry}
                </Button>
              ) : null}
              {canDiscard(item, who) && item.state === 'refused' ? (
                <Button
                  variant="outline"
                  className="rounded-sm"
                  aria-describedby={named}
                  onClick={() => ask([item.id])}
                >
                  <Trash2 aria-hidden="true" className="size-4" />
                  {m.capture.discard}
                </Button>
              ) : null}
            </li>
          )
        })}
      </ul>
      {dialog}
    </>
  )
}

/** At the return, a damage marked now goes into the charges: once, by the counter's choice. */
function ChargeAction({
  damage,
  m,
  onCharge,
  charged,
  full,
}: {
  damage: Damage
  m: Messages
  onCharge?: ((damage: MarkedDamage) => void) | undefined
  charged?: ReadonlySet<string> | undefined
  full: boolean
}) {
  // Already in the charges: the damage's badge says so.
  if (!onCharge || !damage.newOnThisRental || damage.chargedCents !== null) return null
  if (charged?.has(damage.id)) return null
  // No button that does nothing: at the limit, say why it cannot join.
  if (full) return <p className="self-center text-sm">{m.inspection.chargesFull}</p>
  return (
    <div>
      <Button
        variant="outline"
        className="rounded-sm"
        aria-label={format(m.inspection.chargeLabel, { description: damage.description })}
        onClick={() =>
          onCharge({ id: damage.id, zone: damage.zone, description: damage.description })
        }
      >
        <Plus aria-hidden="true" className="size-4" />
        {m.inspection.charge}
      </Button>
    </div>
  )
}

/**
 * Photos, each opening in full; `wide` for a list across the whole card. The
 * web app links to each photo; the iPad app fetches them with its session and
 * opens one in its own viewer (A1).
 */
export function Thumbs({
  photos,
  alt,
  empty,
  wide = false,
}: {
  photos: readonly Photo[]
  alt: (photo: Photo, index: number) => string
  empty: string
  wide?: boolean
}) {
  const [shown, setShown] = useState<{ src: string; alt: string } | null>(null)
  if (photos.length === 0) {
    return (
      <p className="rounded-sm border border-dashed border-rule px-4 py-6 text-center text-sm text-text-muted">
        {empty}
      </p>
    )
  }
  return (
    <>
      <ul className={cn('grid gap-2', wide ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2')}>
        {photos.map((photo, index) => {
          const file = { kind: 'photo', mediaId: photo.mediaId } as const
          return (
            <li key={photo.id}>
              {AUTH_MODE === 'cookie' ? (
                <a href={filePath(file)} target="_blank" rel="noopener" className="block">
                  <img
                    src={filePath(file)}
                    alt={alt(photo, index)}
                    loading="lazy"
                    className="aspect-4/3 w-full rounded-sm border border-rule bg-bg object-cover"
                  />
                </a>
              ) : (
                <PhotoThumb
                  mediaId={photo.mediaId}
                  alt={alt(photo, index)}
                  onOpen={(src) => setShown({ src, alt: alt(photo, index) })}
                />
              )}
            </li>
          )
        })}
      </ul>
      {shown ? (
        <ImageViewer src={shown.src} alt={shown.alt} onClose={() => setShown(null)} />
      ) : null}
    </>
  )
}

/**
 * One photo: taken with the iPad's camera, re-encoded on it (photos/jpeg.ts)
 * and kept there; it is sent from the queue (S4), signal or not.
 */
function PhotoField({ label, target, full }: { label: string; target: Target; full: boolean }) {
  const { messages: m } = useLocale()
  const { capturePhoto } = useCapture()
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const [invalid, setInvalid] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)

  async function choose(file: File | undefined) {
    setInvalid(null)
    setStatus(null)
    if (!file) return
    setBusy(true)
    try {
      const jpeg = await photoToJpeg(file)
      if (!jpeg) {
        setInvalid(m.inspection.unreadable)
        return
      }
      await capturePhoto({
        key: { ...target.key, plate: target.plate },
        zone: target.zone,
        blob: jpeg,
        ...(target.damageId ? { damageId: target.damageId } : {}),
        ...(target.damageItemId ? { damageItemId: target.damageItemId } : {}),
      })
      setStatus(m.capture.saved)
    } catch (error) {
      if (isFull(error)) setInvalid(m.capture.full)
      else {
        reportError(error, { mechanism: 'capture.photo', handled: true })
        setInvalid(m.errors.client.unexpected)
      }
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  if (full) return <p className="text-sm">{m.capture.limitPhotos}</p>
  return (
    <div className="flex flex-col gap-2">
      <FilePicker
        ref={input}
        // The zone or the damage, for a screen reader; the button's words begin it.
        aria-label={label}
        label={m.inspection.takePhoto}
        icon={<Camera aria-hidden="true" className="size-6" />}
        busy={busy}
        error={invalid}
        className="rounded-sm"
        accept="image/*"
        capture="environment"
        onChange={(event) => void choose(event.target.files?.[0])}
      />
      {/* On screen the photo joining the grid is the confirmation; this tells a screen reader. */}
      <p role="status" className="sr-only">
        {status}
      </p>
    </div>
  )
}

/** A damage on this zone; Enter marks it rather than submitting the form around it. */
function MarkDamage({
  target,
  full,
  onQueued,
}: {
  target: Target
  full: boolean
  onQueued?: ((damage: DamageItem) => void) | undefined
}) {
  const { messages: m } = useLocale()
  const { captureDamage } = useCapture()
  const [text, setText] = useState('')
  // Enter twice, or Enter then the button: one damage, kept once.
  const sending = useRef(false)
  const [busy, setBusy] = useState(false)
  const [invalid, setInvalid] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  async function mark() {
    if (sending.current) return
    const description = text.trim()
    setSaved(null)
    if (!description) {
      setInvalid(m.inspection.describe)
      return
    }
    setInvalid(null)
    sending.current = true
    setBusy(true)
    try {
      const damage = await captureDamage({
        key: { ...target.key, plate: target.plate },
        zone: target.zone,
        description,
      })
      setText('')
      setSaved(format(m.capture.damageSaved, { description }))
      onQueued?.(damage)
    } catch (error) {
      if (isFull(error)) setInvalid(m.capture.full)
      else {
        reportError(error, { mechanism: 'capture.damage', handled: true })
        setInvalid(m.errors.client.unexpected)
      }
    } finally {
      sending.current = false
      setBusy(false)
    }
  }

  if (full) return <p className="text-sm">{m.capture.limitDamages}</p>
  return (
    <div className="flex flex-col gap-3 border-t border-rule pt-4">
      <Field
        label={format(m.inspection.markLabel, { zone: m.inspection.zones[target.zone] })}
        hint={
          target.key.stage === 'return'
            ? m.inspection.markHintReturn
            : m.inspection.markHintCheckout
        }
        error={invalid}
      >
        {(control) => (
          <StaffInput
            {...control}
            maxLength={200}
            autoComplete="off"
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return
              event.preventDefault()
              void mark()
            }}
          />
        )}
      </Field>
      <div>
        <Button variant="staff" busy={busy} onClick={() => void mark()}>
          {m.inspection.mark}
        </Button>
      </div>
      {/* On screen the damage joining the list is the confirmation; this tells a screen reader. */}
      <p role="status" className="sr-only">
        {saved}
      </p>
    </div>
  )
}
